import React, { useEffect, useRef, useState, memo } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { setBranchId } from '../../../../auth/actions';
import PropTypes from 'prop-types';
import TableComp from './Table';
import AddItemPopup from './AddItemPopup';
import { createSupplierInvoice, getAllSuppliers } from '../../actions';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReplacementLink } from '../../../supplierReturn/components/ReplacementLink';
import { ApplyCredit } from '../../../supplierReturn/components/ApplyCredit';
import { showAlert } from '../../../../../common/actions';
import { Button, Input, Dropdown, SearchableSelect } from '../../../../../common/ui';

const hasInvalidApplyAmount = (amounts) => Object.values(amounts || {}).some((raw) => {
  const text = String(raw ?? '').trim();
  if (!text) return false;
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return true;
  return Math.round(parsed * 100) / 100 < 0;
});

const PAYMENT_METHODS = [
  { id: 1, name: 'Cash' },
  { id: 2, name: 'Card' },
  { id: 3, name: 'Cheque' },
  { id: 4, name: 'Credit' },
];

const paymentOptions = PAYMENT_METHODS.map((p) => ({ value: p.name, label: p.name }));

const branchOptions = (branchList) =>
  (branchList || []).map((item) => ({ value: item.id, label: item.name }));

const AddNewInvoice = memo(
  ({
    setBranchId,
    auth: { branchList, branchId },
    getAllSuppliers,
    supplierInvoiceState: { allSuppliers },
    showAlert,
    createSupplierInvoice,
  }) => {
    const location = useLocation();
    const [relatedSupplierReturnId, setRelatedSupplierReturnId] = useState('');
    const [replacementReturnNo, setReplacementReturnNo] = useState('');
    const [appliedSupplierReturns, setAppliedSupplierReturns] = useState([]);
    const [applyAmounts, setApplyAmounts] = useState({});
    const [applyInvalid, setApplyInvalid] = useState(false);
    const [isOpenAddInvoicePopup, setOpenAddInvoicePopup] = useState(false);
    const [supplierInvoiceForm, setSupplierInvoiceForm] = useState({
      invoiceNo: null,
      supplierId: null,
      totalAmount: 0,
      totalDiscount: 0,
      totalPayable: 0,
      paidAmount: 0,
      balanceAmount: 0,
      paymentMethod: null,
      companyId: null,
      branchId: null,
      stockList: [],
    });

    useEffect(() => {
      const state = location.state || {};
      if (state.relatedSupplierReturnId) {
        setRelatedSupplierReturnId(String(state.relatedSupplierReturnId));
        setReplacementReturnNo(state.returnNo || '');
      } else {
        setRelatedSupplierReturnId('');
        setReplacementReturnNo('');
      }
      if (state.supplierId) {
        setSupplierInvoiceForm((prev) => (
          prev.supplierId ? prev : { ...prev, supplierId: state.supplierId }
        ));
      }
    }, [location.state]);

    const [isLoading, setLoading] = useState(false);
    const submittingRef = useRef(false);
    const navigate = useNavigate();

    const handleChangeBranch = (value) => {
      setBranchId(value);
    };

    const onClickAddItem = () => {
      setOpenAddInvoicePopup(true);
    };

    const onChangeInvoiceNo = (value) => {
      setSupplierInvoiceForm((prev) => ({ ...prev, invoiceNo: value || null }));
    };

    const onSelectPaymentMethod = (value) => {
      setSupplierInvoiceForm((prev) => ({ ...prev, paymentMethod: value || null }));
    };

    const onChangePaidAmount = (value) => {
      const paidAmount = parseFloat(value) || 0;
      const balanceAmount = paidAmount - supplierInvoiceForm.totalPayable;
      setSupplierInvoiceForm((prev) => ({
        ...prev,
        paidAmount,
        balanceAmount: Number.isFinite(balanceAmount) ? balanceAmount : 0,
      }));
    };

    useEffect(() => {
      getAllSuppliers();
    }, [getAllSuppliers]);

    const addStockToInvoice = (invoiceStock) => {
      const newStockList = [...supplierInvoiceForm.stockList, invoiceStock];
      let newTotalAmount = 0;
      let newTotalPayable = 0;
      for (const stockItem of newStockList) {
        newTotalAmount += stockItem.unitPrice * stockItem.qty;
        newTotalPayable += stockItem.totalAmount;
      }
      const newTotalDiscount = newTotalAmount - newTotalPayable;
      let newBalanceAmount = 0;
      if (supplierInvoiceForm.paidAmount > 0) {
        newBalanceAmount = supplierInvoiceForm.paidAmount - newTotalPayable;
        newBalanceAmount = Number.isFinite(newBalanceAmount) ? newBalanceAmount : 0;
      }
      setSupplierInvoiceForm((prev) => ({
        ...prev,
        totalAmount: newTotalAmount,
        totalDiscount: newTotalDiscount,
        totalPayable: newTotalPayable,
        balanceAmount: newBalanceAmount,
        stockList: newStockList,
      }));
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      if (submittingRef.current) return;
      submittingRef.current = true;
      setLoading(true);
      try {
      if (!supplierInvoiceForm.supplierId) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Please Select a Supplier!',
        });
        return;
      }
      if (!supplierInvoiceForm.paymentMethod) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Please Select a Payment Method!',
        });
        return;
      }
      if (supplierInvoiceForm.stockList.length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Please add an Item to the Invoice!',
        });
        return;
      }
      if (applyInvalid || hasInvalidApplyAmount(applyAmounts)) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Enter a valid Apply amount, or leave the box blank.',
        });
        return;
      }

      const invoiceItemList = supplierInvoiceForm.stockList.map((item) => {
        const { product, ...rest } = item;
        return rest;
      });
      const supplierInvoiceObj = {
        ...supplierInvoiceForm,
        stockList: invoiceItemList,
      };
      if (relatedSupplierReturnId) {
        supplierInvoiceObj.relatedSupplierReturnId = relatedSupplierReturnId;
      } else {
        delete supplierInvoiceObj.relatedSupplierReturnId;
      }
      if (appliedSupplierReturns.length > 0) {
        supplierInvoiceObj.appliedSupplierReturns = appliedSupplierReturns;
      } else {
        delete supplierInvoiceObj.appliedSupplierReturns;
      }

      const isSuccessCreatedInvoice = await createSupplierInvoice(supplierInvoiceObj);

      if (isSuccessCreatedInvoice === true) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Supplier Invoice Created Successfully !',
        });
        navigate('/inventory/invoices');
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: typeof isSuccessCreatedInvoice === 'string' && isSuccessCreatedInvoice.trim()
            ? isSuccessCreatedInvoice
            : 'Operation Failed. Please try again later !',
        });
      }
      } finally {
        submittingRef.current = false;
        setLoading(false);
      }
    };

    const supplierOptions = Array.isArray(allSuppliers) ? allSuppliers : [];
    const balanceDisplay =
      Number.isFinite(supplierInvoiceForm.balanceAmount) ? supplierInvoiceForm.balanceAmount : 0;

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>New Invoice</h1>
            <p className={styles.breadcrumb}>Inventory / Invoices / New</p>
          </div>
          {branchList && branchList.length > 0 && branchId && (
            <div className={styles.branchSelectWrap}>
              <Dropdown
                label="Branch"
                options={branchOptions(branchList)}
                value={branchId}
                onChange={handleChangeBranch}
                placeholder="Select branch"
              />
            </div>
          )}
        </div>
        <div className={styles.body}>
          <form className={styles.formCard} onSubmit={onSubmit} noValidate>
            <ReplacementLink
              supplierId={supplierInvoiceForm.supplierId}
              value={relatedSupplierReturnId}
              returnNo={replacementReturnNo}
              onChange={(nextId, nextNo) => {
                setRelatedSupplierReturnId(nextId);
                setReplacementReturnNo(nextNo);
              }}
            />
            {supplierInvoiceForm.supplierId ? (
              <ApplyCredit
                supplierId={supplierInvoiceForm.supplierId}
                excludeId={relatedSupplierReturnId}
                amounts={applyAmounts}
                onAmountsChange={setApplyAmounts}
                onChange={(rows, invalid) => {
                  setAppliedSupplierReturns(rows);
                  setApplyInvalid(Boolean(invalid));
                }}
              />
            ) : null}
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <Input
                  label="Invoice Number"
                  type="text"
                  value={supplierInvoiceForm.invoiceNo ?? ''}
                  onChange={onChangeInvoiceNo}
                />
              </div>
              <div className={styles.fieldGroup}>
                <SearchableSelect
                  label="Supplier"
                  options={supplierOptions}
                  getOptionLabel={(o) => o.companyname}
                  getOptionValue={(o) => o.id}
                  value={supplierInvoiceForm.supplierId}
                  onChange={(id) => {
                    if (String(supplierInvoiceForm.supplierId || '') !== String(id || '')) {
                      setRelatedSupplierReturnId('');
                      setReplacementReturnNo('');
                      setApplyAmounts({});
                      setAppliedSupplierReturns([]);
                      setApplyInvalid(false);
                    }
                    setSupplierInvoiceForm((prev) => ({ ...prev, supplierId: id }));
                  }}
                  onSelect={(opt) => {
                    const nextId = opt?.id ?? null;
                    if (String(supplierInvoiceForm.supplierId || '') !== String(nextId || '')) {
                      setRelatedSupplierReturnId('');
                      setReplacementReturnNo('');
                      setApplyAmounts({});
                      setAppliedSupplierReturns([]);
                      setApplyInvalid(false);
                    }
                    setSupplierInvoiceForm((prev) => ({ ...prev, supplierId: nextId }));
                  }}
                  placeholder="Not selected"
                />
              </div>
              <div className={styles.fieldGroup}>
                <Dropdown
                  label="Payment Method"
                  options={paymentOptions}
                  value={supplierInvoiceForm.paymentMethod}
                  onChange={onSelectPaymentMethod}
                  placeholder="Not selected"
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Total Amount"
                  type="text"
                  value={String(supplierInvoiceForm.totalAmount)}
                  disabled
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Discount"
                  type="text"
                  value={String(supplierInvoiceForm.totalDiscount)}
                  disabled
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Payable"
                  type="text"
                  value={String(supplierInvoiceForm.totalPayable)}
                  disabled
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Paid"
                  type="number"
                  value={supplierInvoiceForm.paidAmount != null ? String(supplierInvoiceForm.paidAmount) : ''}
                  onChange={onChangePaidAmount}
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Balance"
                  type="text"
                  value={String(balanceDisplay)}
                  disabled
                />
              </div>
            </div>

            <div className={styles.addItemRow}>
              <Button type="button" onClick={onClickAddItem}>
                Add Item
              </Button>
              {isOpenAddInvoicePopup && (
                <AddItemPopup
                  addStockToInvoice={addStockToInvoice}
                  isOpenAddInvoicePopup={isOpenAddInvoicePopup}
                  setOpenAddInvoicePopup={setOpenAddInvoicePopup}
                />
              )}
            </div>

            <div className={styles.chartDiv}>
              <TableComp invoiceStockData={supplierInvoiceForm.stockList} />
            </div>

            <div className={styles.buttonPanel}>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving…' : 'Submit'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/inventory/invoices')}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

AddNewInvoice.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  getAllSuppliers: PropTypes.func.isRequired,
  supplierInvoiceState: PropTypes.shape({ allSuppliers: PropTypes.array }).isRequired,
  showAlert: PropTypes.func.isRequired,
  createSupplierInvoice: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  supplierInvoiceState: state.supplierInvoiceState,
});

export default connect(mapStateToProps, {
  setBranchId,
  getAllSuppliers,
  showAlert,
  createSupplierInvoice,
})(AddNewInvoice);
